/*!
 * Copyright (c) 2020 CreditSense <mikes@creditsense.ai>
 *
 * This software is private and is unlicensed. Please, contact
 * author for any licensing details.
 */
import React, { useCallback, useEffect } from 'react';
import Box from '@mui/material/Box';
import asyncLogger from '../common/logger';

const RECAPTCHA_REFRESH_TOKEN_INTERVAL_SEC = 90;

export interface ReCaptchaProps {
    siteKey: string;
    onVerify: (token: string) => void | Promise<void>;
    action?: string;
    language?: string;
    /**
     * Use this to provide unique identifier for captcha session, so it
     * would be re-loaded to make sure you won't get EXPIRED token validation
     * error when checking captcha related action on server.
     */
    sessionId?: string;
}

interface ReCaptchaEnterprise {
    execute: (siteKey: string, options: { action: string }) => Promise<string>;
    ready: (cb: (...args: any[]) => any) => any;
}

export const ReCaptcha: React.FunctionComponent<ReCaptchaProps> = props => {
    const scriptId = 're-captcha-v3';

    const onLoad = useCallback(() => {
        const reCaptcha = (window as any)
            .grecaptcha?.enterprise as ReCaptchaEnterprise
        ;

        if (!reCaptcha) {
            return asyncLogger.warn('Google ReCaptcha was not loaded!');
        }

        const setToken = async () => reCaptcha.ready(async () => {
            try {
                const result = await (reCaptcha as any).execute(
                    props.siteKey,
                    { action: props.action || 'common' },
                );

                props.onVerify && props.onVerify(result);
            } catch (err) {
                asyncLogger.error('ReCaptcha execute error:', err);
            }
        });

        setToken();

        const intervalId = setInterval(
            setToken,
            RECAPTCHA_REFRESH_TOKEN_INTERVAL_SEC * 1000,
        );

        return () => clearInterval(intervalId);

        // eslint-disable-next-line
    }, [props.action, props.onVerify, props.siteKey]);

    useEffect(() => {
        const script = document.getElementById(scriptId);

        if (script) {
            script.parentNode?.removeChild(script);
            delete (window as any).grecaptcha;
        }
    }, [props.sessionId]);

    useEffect(() => {
        if (!document.getElementById(scriptId) && !(window as any).grecaptcha) {
            const script = document.createElement('script');

            script.addEventListener('load', onLoad);
            script.setAttribute('id', scriptId);

            document.getElementsByTagName('head')[0].appendChild(script);

            script.setAttribute('src',
                'https://www.google.com/recaptcha/enterprise.js?render=' +
                props.siteKey +
                (props.language ? `&hl=${ props.language }` : ''),
            );
        }
    }, [onLoad, props.language, props.siteKey, props.sessionId]);

    useEffect(() => {
        return () => {
            // noinspection SpellCheckingInspection
            const [captcha] = document.getElementsByClassName(
                'grecaptcha-badge',
            );
            const script = document.getElementById(scriptId);

            if (captcha && script) {
                const scriptParent = script?.parentNode;

                scriptParent?.removeChild(script);
                delete (window as any).grecaptcha;

                const parent = captcha.parentNode;

                parent?.removeChild(captcha);
            }
        };
    }, []);

    return <Box
        className="g-recaptcha"
        data-sitekey={ props.siteKey }
        data-size="invisible"
        sx={{ padding: '0 45px 0 53px', maxWidth: '440px' }}
    />;
};
